/**
 * AddAdminIdSection — 3-step form to register a new supervisor via UAE Emirates ID.
 *   Step 1: Upload front image
 *   Step 2: Upload back image + OCR extract
 *   Step 3: Review + manual fields + submit
 * Self-contained: state lives inside; only language / jobTitles / axios config / onSuccess come from parent.
 * Extracted from AccountSettings.js (~432 inline lines)
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserPlus, FileText, X, ArrowRight, CheckCircle, Loader2,
  Sparkles, RotateCcw,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import PhoneInput, { validateNationalNumber, PHONE_COUNTRIES } from '../../../components/PhoneInput';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEYS = {
  step: 'id_formStep',
  data: 'id_formData',
  front: 'id_frontPreview',
  back: 'id_backPreview',
};

const initialFormData = {
  full_name_ar: '',
  full_name_en: '',
  first_name: '',
  last_name: '',
  id_number: '',
  date_of_birth: '',
  expiry_date: '',
  nationality_ar: '',
  nationality_en: '',
  email: '',
  phone: '',
  job_title_id: '',
  employee_id_number: '',
};

function clearIdFormStorage() {
  Object.values(STORAGE_KEYS).forEach((k) => sessionStorage.removeItem(k));
}

export default function AddAdminIdSection({ language, jobTitles, headers, onSuccess }) {
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(() => sessionStorage.getItem(STORAGE_KEYS.front));
  const [backPreview, setBackPreview] = useState(() => sessionStorage.getItem(STORAGE_KEYS.back));
  const [extractingData, setExtractingData] = useState(false);
  const [, setExtractedData] = useState(null);
  const [creatingFromId, setCreatingFromId] = useState(false);
  const [idFormData, setIdFormData] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEYS.data);
    return saved ? { ...initialFormData, ...JSON.parse(saved) } : initialFormData;
  });
  const [idFormStep, setIdFormStep] = useState(() => {
    const saved = sessionStorage.getItem(STORAGE_KEYS.step);
    return saved ? parseInt(saved, 10) : 1;
  });

  // Persist to sessionStorage so navigation doesn't lose progress
  useEffect(() => { sessionStorage.setItem(STORAGE_KEYS.step, idFormStep.toString()); }, [idFormStep]);
  useEffect(() => { sessionStorage.setItem(STORAGE_KEYS.data, JSON.stringify(idFormData)); }, [idFormData]);
  useEffect(() => { if (frontPreview) sessionStorage.setItem(STORAGE_KEYS.front, frontPreview); }, [frontPreview]);
  useEffect(() => { if (backPreview) sessionStorage.setItem(STORAGE_KEYS.back, backPreview); }, [backPreview]);

  const handleFrontImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFrontImage(file);
      setFrontPreview(URL.createObjectURL(file));
    }
  };

  const handleBackImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackImage(file);
      setBackPreview(URL.createObjectURL(file));
    }
  };

  const extractEmiratesIdData = useCallback(async () => {
    if (!frontImage) {
      toast.error(language === 'ar' ? 'يرجى رفع صورة الوجه الأمامي للهوية' : 'Please upload front ID image');
      return;
    }
    if (!backImage) {
      toast.error(language === 'ar' ? 'يرجى رفع صورة الوجه الخلفي للهوية' : 'Please upload back ID image');
      return;
    }
    setExtractingData(true);
    try {
      const formData = new FormData();
      formData.append('front_image', frontImage);
      formData.append('back_image', backImage);

      const res = await axios.post(`${BACKEND_URL}/api/ocr/emirates-id`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setExtractedData(data);
        const nameParts = (data.full_name_en || '').trim().split(/\s+/);
        setIdFormData((prev) => ({
          ...prev,
          full_name_ar: data.full_name_ar || '',
          full_name_en: data.full_name_en || '',
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(-1)[0] || '',
          id_number: data.id_number || '',
          date_of_birth: data.date_of_birth || '',
          expiry_date: data.expiry_date || '',
          nationality_ar: data.nationality_ar || (data.nationality && /[\u0600-\u06FF]/.test(data.nationality) ? data.nationality : '') || '',
          nationality_en: data.nationality_en || (data.nationality && !/[\u0600-\u06FF]/.test(data.nationality) ? data.nationality : '') || '',
        }));
        setIdFormStep(3);
        toast.success(language === 'ar' ? 'تم استخراج البيانات بنجاح' : 'Data extracted successfully');
      }
    } catch (err) {
      console.error('Error extracting ID data:', err);
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في قراءة الهوية' : 'Failed to read ID'));
    } finally {
      setExtractingData(false);
    }
  }, [frontImage, backImage, headers, language]);

  const resetForm = useCallback(() => {
    clearIdFormStorage();
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview(null);
    setBackPreview(null);
    setExtractedData(null);
    setIdFormStep(1);
    setIdFormData(initialFormData);
  }, []);

  const handleCreateAdminFromId = async () => {
    if (!idFormData.full_name_en || !idFormData.email || !idFormData.phone) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    // Phone validation — must be a valid UAE number in E.164 form
    const country = PHONE_COUNTRIES[0];
    const nationalPart = idFormData.phone.startsWith(country.dialCode)
      ? idFormData.phone.slice(country.dialCode.length)
      : idFormData.phone.replace(/^\+/, '');
    if (!validateNationalNumber(nationalPart, country).valid) {
      toast.error(language === 'ar' ? 'رقم الهاتف غير صالح' : 'Invalid phone number');
      return;
    }
    if (!idFormData.employee_id_number || idFormData.employee_id_number.length < 3) {
      toast.error(language === 'ar' ? 'يرجى إدخال الرقم الوظيفي (3 أرقام على الأقل)' : 'Please enter Employee ID (at least 3 digits)');
      return;
    }
    // المسمى الوظيفي إلزامي — هو الـ Role الذي تُبنى عليه الصلاحيات
    if (!idFormData.job_title_id) {
      toast.error(language === 'ar' ? 'المسمى الوظيفي إلزامي — كل موظف يجب أن يكون له مسمى (Role)' : 'Job title is required — every employee must have a role');
      return;
    }
    setCreatingFromId(true);
    try {
      const selectedTitle = jobTitles.find((t) => t.id === idFormData.job_title_id);
      const employeeId = `ASB${idFormData.employee_id_number}`;

      // --- Upload Emirates ID images first so they become persistent attachments ---
      let idFrontFileId = null;
      let idBackFileId = null;
      try {
        if (frontImage) {
          const fd = new FormData();
          fd.append('file', frontImage, frontImage.name || 'eid_front.jpg');
          const up = await axios.post(`${BACKEND_URL}/api/upload/file`, fd, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          });
          idFrontFileId = up.data?.file_id || null;
        }
        if (backImage) {
          const fd = new FormData();
          fd.append('file', backImage, backImage.name || 'eid_back.jpg');
          const up = await axios.post(`${BACKEND_URL}/api/upload/file`, fd, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          });
          idBackFileId = up.data?.file_id || null;
        }
      } catch (uErr) {
        // Soft-fail: registration proceeds without attachments if upload fails
        console.warn('Attachment upload failed (continuing without attachments):', uErr);
      }

      const supervisorData = {
        email: idFormData.email,
        phone: idFormData.phone,
        first_name: idFormData.first_name || idFormData.full_name_en.split(' ')[0],
        last_name: idFormData.last_name || idFormData.full_name_en.split(' ').slice(-1)[0],
        full_name_ar: idFormData.full_name_ar,
        full_name_en: idFormData.full_name_en,
        id_number: idFormData.id_number,
        date_of_birth: idFormData.date_of_birth,
        expiry_date: idFormData.expiry_date,
        nationality_ar: idFormData.nationality_ar,
        nationality_en: idFormData.nationality_en,
        job_title: selectedTitle ? selectedTitle.name_en : '',
        job_title_ar: selectedTitle ? selectedTitle.name_ar : '',
        job_title_id: idFormData.job_title_id || '',
        employee_id: employeeId,
        id_front_file_id: idFrontFileId,
        id_back_file_id: idBackFileId,
        preferred_language: idFormData.preferred_language || 'ar',
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/register-supervisor`, supervisorData, { headers });
      if (res.data.success) {
        toast.success(language === 'ar'
          ? `تم إدراج الموظف بنجاح! الرقم الوظيفي: ${employeeId}. يمكنك الآن إنشاء حساب يوزر له.`
          : `Employee registered successfully! Employee ID: ${employeeId}. You can now create a user account.`,
        );
      }
      resetForm();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في إدراج الموظف' : 'Failed to register employee'));
    } finally {
      setCreatingFromId(false);
    }
  };

  const isStep3SubmitDisabled = (
    creatingFromId
    || !idFormData.first_name
    || !idFormData.last_name
    || !idFormData.email
    || !idFormData.phone
    || !idFormData.employee_id_number
    || idFormData.employee_id_number.length < 3
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="add-admin-id-section"
    >
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-[#d4af37]" />
          {language === 'ar' ? 'إدراج مشرف جديد عبر بطاقة الهوية الإماراتية' : 'Add New Supervisor via Emirates ID'}
        </h2>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${idFormStep >= 1 ? 'text-[#d4af37]' : 'text-[#71717A]'}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${idFormStep >= 1 ? 'bg-[#d4af37] text-black' : 'bg-white/10'}`}>1</span>
            <span className="text-sm hidden sm:block">{language === 'ar' ? 'الوجه الأمامي' : 'Front Side'}</span>
          </div>
          <div className={`w-8 h-0.5 ${idFormStep >= 2 ? 'bg-[#d4af37]' : 'bg-white/10'}`} />
          <div className={`flex items-center gap-2 ${idFormStep >= 2 ? 'text-[#d4af37]' : 'text-[#71717A]'}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${idFormStep >= 2 ? 'bg-[#d4af37] text-black' : 'bg-white/10'}`}>2</span>
            <span className="text-sm hidden sm:block">{language === 'ar' ? 'الوجه الخلفي' : 'Back Side'}</span>
          </div>
          <div className={`w-8 h-0.5 ${idFormStep >= 3 ? 'bg-[#d4af37]' : 'bg-white/10'}`} />
          <div className={`flex items-center gap-2 ${idFormStep >= 3 ? 'text-emerald-400' : 'text-[#71717A]'}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${idFormStep >= 3 ? 'bg-emerald-500 text-white' : 'bg-white/10'}`}>3</span>
            <span className="text-sm hidden sm:block">{language === 'ar' ? 'البيانات' : 'Data'}</span>
          </div>
        </div>

        {/* Step 1: Front Image */}
        {idFormStep === 1 && (
          <div className="text-center">
            <h3 className="text-lg font-bold text-white mb-4">
              {language === 'ar' ? 'الخطوة 1: رفع الوجه الأمامي للهوية' : 'Step 1: Upload Front Side of ID'}
            </h3>
            <div className={`relative max-w-md mx-auto bg-[#d4af37]/5 border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${frontPreview ? 'border-emerald-500/50' : 'border-[#d4af37]/30 hover:border-[#d4af37]/50'}`}>
              {frontPreview ? (
                <div className="relative">
                  <img src={frontPreview} alt="Front ID" className="w-full h-48 object-contain rounded-xl" />
                  <button
                    onClick={() => { setFrontImage(null); setFrontPreview(null); }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <p className="text-emerald-400 text-sm mt-3">{language === 'ar' ? 'تم رفع الوجه الأمامي ✓' : 'Front Side Uploaded ✓'}</p>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 bg-[#d4af37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-10 w-10 text-[#d4af37]" />
                  </div>
                  <h4 className="text-white font-medium mb-2">{language === 'ar' ? 'الوجه الأمامي للهوية' : 'Front Side of ID'}</h4>
                  <p className="text-[#71717A] text-sm mb-4">{language === 'ar' ? 'صورة واضحة تظهر الصورة الشخصية والاسم' : 'Clear image showing photo and name'}</p>
                  <input type="file" accept="image/*" onChange={handleFrontImageChange} className="hidden" id="front-id-upload" />
                  <label htmlFor="front-id-upload" className="inline-flex items-center gap-2 px-6 py-3 bg-[#d4af37] hover:bg-[#c9a227] text-black font-semibold rounded-xl cursor-pointer transition-colors">
                    <FileText className="h-5 w-5" />
                    {language === 'ar' ? 'اختيار صورة' : 'Select Image'}
                  </label>
                </>
              )}
            </div>
            {frontPreview && (
              <Button onClick={() => setIdFormStep(2)} className="mt-6 bg-[#d4af37] hover:bg-[#c9a227] text-black px-8">
                {language === 'ar' ? 'التالي' : 'Next'}
                <ArrowRight className="h-4 w-4 me-2 rotate-180" />
              </Button>
            )}
          </div>
        )}

        {/* Step 2: Back Image */}
        {idFormStep === 2 && (
          <div className="text-center">
            <h3 className="text-lg font-bold text-white mb-4">
              {language === 'ar' ? 'الخطوة 2: رفع الوجه الخلفي للهوية' : 'Step 2: Upload Back Side of ID'}
            </h3>
            <div className={`relative max-w-md mx-auto bg-blue-500/5 border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${backPreview ? 'border-emerald-500/50' : 'border-blue-500/30 hover:border-blue-500/50'}`}>
              {backPreview ? (
                <div className="relative">
                  <img src={backPreview} alt="Back ID" className="w-full h-48 object-contain rounded-xl" />
                  <button
                    onClick={() => { setBackImage(null); setBackPreview(null); }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <p className="text-emerald-400 text-sm mt-3">{language === 'ar' ? 'تم رفع الوجه الخلفي ✓' : 'Back Side Uploaded ✓'}</p>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-10 w-10 text-blue-400" />
                  </div>
                  <h4 className="text-white font-medium mb-2">{language === 'ar' ? 'الوجه الخلفي للهوية' : 'Back Side of ID'}</h4>
                  <p className="text-[#71717A] text-sm mb-4">{language === 'ar' ? 'صورة واضحة للوجه الخلفي' : 'Clear image of back side'}</p>
                  <input type="file" accept="image/*" onChange={handleBackImageChange} className="hidden" id="back-id-upload" />
                  <label htmlFor="back-id-upload" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl cursor-pointer transition-colors">
                    <FileText className="h-5 w-5" />
                    {language === 'ar' ? 'اختيار صورة' : 'Select Image'}
                  </label>
                </>
              )}
            </div>

            <div className="flex items-center justify-center gap-4 mt-6">
              <Button onClick={() => setIdFormStep(1)} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <ArrowRight className="h-4 w-4 ms-2" />
                {language === 'ar' ? 'السابق' : 'Previous'}
              </Button>
              {backPreview && (
                <Button onClick={extractEmiratesIdData} disabled={extractingData} className="bg-emerald-500 hover:bg-emerald-600 text-white px-8">
                  {extractingData ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin me-2" />
                      {language === 'ar' ? 'جاري قراءة البيانات...' : 'Reading Data...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'قراءة البيانات' : 'Extract Data'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Review & Submit */}
        {idFormStep === 3 && (
          <div>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
              {language === 'ar' ? 'الخطوة 3: مراجعة وتعديل البيانات' : 'Step 3: Review & Edit Data'}
            </h3>
            <p className="text-[#71717A] text-sm mb-6">
              {language === 'ar' ? 'تم استخراج البيانات من الهوية. يمكنك التعديل عليها قبل إضافة المشرف.' : 'Data extracted from ID. You can edit before adding the supervisor.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الاسم الكامل (من الهوية)' : 'Full Name (from ID)'}</label>
                <Input value={idFormData.full_name_ar} onChange={(e) => setIdFormData({ ...idFormData, full_name_ar: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="rtl" />
                <p className="text-xs text-[#606060] mt-1">{language === 'ar' ? 'سيُستخدم كبيانات مرجعية' : 'Will be used as reference data'}</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الاسم الكامل بالإنجليزية' : 'Full Name (English)'}</label>
                <Input
                  value={idFormData.full_name_en}
                  onChange={(e) => {
                    const fullName = e.target.value;
                    const nameParts = fullName.trim().split(/\s+/);
                    setIdFormData({
                      ...idFormData,
                      full_name_en: fullName,
                      first_name: nameParts[0] || '',
                      last_name: nameParts.slice(-1)[0] || '',
                    });
                  }}
                  className="bg-white/5 border-white/10 text-white"
                  dir="ltr"
                />
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الاسم الأول *' : 'First Name *'}</label>
                <Input value={idFormData.first_name} onChange={(e) => setIdFormData({ ...idFormData, first_name: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الاسم الأخير *' : 'Last Name *'}</label>
                <Input value={idFormData.last_name} onChange={(e) => setIdFormData({ ...idFormData, last_name: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'رقم الهوية' : 'ID Number'}</label>
                <Input value={idFormData.id_number} onChange={(e) => setIdFormData({ ...idFormData, id_number: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" placeholder="784-XXXX-XXXXXXX-X" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth'}</label>
                <Input value={idFormData.date_of_birth} onChange={(e) => setIdFormData({ ...idFormData, date_of_birth: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" placeholder="DD/MM/YYYY" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'تاريخ انتهاء الهوية' : 'ID Expiry Date'}</label>
                <Input value={idFormData.expiry_date} onChange={(e) => setIdFormData({ ...idFormData, expiry_date: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" placeholder="DD/MM/YYYY" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الجنسية (عربي)' : 'Nationality (Arabic)'}</label>
                <Input
                  value={idFormData.nationality_ar}
                  onChange={(e) => setIdFormData({ ...idFormData, nationality_ar: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  dir="rtl"
                  placeholder="إماراتي"
                  data-testid="input-nationality-ar"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الجنسية (إنجليزي)' : 'Nationality (English)'}</label>
                <Input
                  value={idFormData.nationality_en}
                  onChange={(e) => setIdFormData({ ...idFormData, nationality_en: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  dir="ltr"
                  placeholder="United Arab Emirates"
                  data-testid="input-nationality-en"
                />
              </div>

              <div className="md:col-span-2 border-t border-white/10 my-2" />
              <p className="md:col-span-2 text-[#d4af37] text-sm font-medium">{language === 'ar' ? 'البيانات اليدوية (مطلوبة)' : 'Manual Data (Required)'}</p>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'الرقم الوظيفي *' : 'Employee ID *'}</label>
                <div className="flex items-center gap-2">
                  <span className="bg-[#d4af37]/20 text-[#d4af37] px-3 py-2 rounded-md font-mono font-bold">ASB</span>
                  <Input
                    value={idFormData.employee_id_number || ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                      setIdFormData({ ...idFormData, employee_id_number: value });
                    }}
                    className="bg-white/5 border-white/10 text-white flex-1 font-mono"
                    dir="ltr" placeholder="000000" minLength={3} maxLength={8}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const randomNum = Math.floor(100000 + Math.random() * 900000).toString();
                      setIdFormData({ ...idFormData, employee_id_number: randomNum });
                    }}
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 px-4"
                  >
                    {language === 'ar' ? 'توليد' : 'Generate'}
                  </Button>
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar'
                    ? `الرقم الوظيفي: ASB${idFormData.employee_id_number || '______'} (3-8 أرقام)`
                    : `Employee ID: ASB${idFormData.employee_id_number || '______'} (3-8 digits)`}
                </p>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'البريد الإلكتروني *' : 'Email *'}</label>
                <Input type="email" value={idFormData.email} onChange={(e) => setIdFormData({ ...idFormData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" dir="ltr" placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'رقم الهاتف *' : 'Phone Number *'}</label>
                <PhoneInput
                  value={idFormData.phone}
                  onChange={(v) => setIdFormData({ ...idFormData, phone: v })}
                  language={language}
                  dataTestidPrefix="register-supervisor-phone"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">{language === 'ar' ? 'المسمى الوظيفي *' : 'Job Title *'}</label>
                <Select value={idFormData.job_title_id || undefined} onValueChange={(v) => setIdFormData({ ...idFormData, job_title_id: v })}>
                  <SelectTrigger className="w-full h-10 bg-[#1a1a2e] border-white/10 text-white focus:ring-2 focus:ring-[#d4af37]/50" data-testid="job-title-trigger">
                    <SelectValue placeholder={language === 'ar' ? 'اختر المسمى الوظيفي' : 'Select Job Title'} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-white/10 text-white">
                    {jobTitles.map((title) => (
                      <SelectItem key={title.id} value={title.id} className="text-white focus:bg-[#d4af37]/20 focus:text-white data-[highlighted]:bg-[#d4af37]/20 data-[highlighted]:text-white">
                        {language === 'ar' ? title.name_ar : title.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-[#A1A1AA] mb-2">
                  {language === 'ar' ? 'اللغة المفضلة (للإيميلات) *' : 'Preferred Language (for emails) *'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { code: 'ar', label: '🇸🇦 العربية' },
                    { code: 'en', label: '🇬🇧 English' },
                  ].map((opt) => {
                    const active = (idFormData.preferred_language || 'ar') === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => setIdFormData({ ...idFormData, preferred_language: opt.code })}
                        data-testid={`id-lang-${opt.code}`}
                        className={`h-11 rounded-xl border text-sm font-medium transition-all ${
                          active
                            ? 'bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]'
                            : 'bg-white/5 border-white/10 text-[#808080] hover:bg-white/10'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#606060] mt-1">
                  {language === 'ar'
                    ? 'سيُرسل إيميل التفعيل وكل رسائل النظام بهذه اللغة.'
                    : 'Activation email + all system emails will be sent in this language.'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
              <Button onClick={resetForm} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <RotateCcw className="h-4 w-4 ms-2" />
                {language === 'ar' ? 'إعادة المحاولة' : 'Start Over'}
              </Button>
              <Button onClick={handleCreateAdminFromId} disabled={isStep3SubmitDisabled} className="bg-emerald-500 hover:bg-emerald-600 px-8" data-testid="submit-supervisor-btn">
                {creatingFromId ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                    {language === 'ar' ? 'جاري الإضافة...' : 'Adding...'}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة موظف/مشرف' : 'Add Employee/Supervisor'}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mt-6">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium text-sm">{language === 'ar' ? 'ملاحظة' : 'Note'}</p>
              <p className="text-xs text-[#808080] mt-1">
                {language === 'ar'
                  ? 'بعد إضافة المشرف، سيصله بريد إلكتروني ترحيبي من ASB يرحب به في عائلة منصات ASB606، ويتضمن بياناته. كما سيتم إرسال بيانات تسجيل الدخول لاحقاً.'
                  : 'After adding the supervisor, they will receive a welcome email from ASB welcoming them to the ASB606 Platforms family, including their data. Login credentials will be sent shortly after.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
